import moment from 'moment';
import Holidays from 'date-holidays';
import { FormProcessData, InsuranceStatus } from 'store/modules/conlicitaseg_store/types';
import { BiddingData } from 'store/modules/bidding_store/types';
import { ProfileData } from 'store/modules/profile_store/types';
import { cleanCnpj, maskCnpj } from 'utils/cnpj';

interface FormatCurrencyOptions {
  emptyOnZero?: boolean;
}

const BRT_UTC_OFFSET_MINUTES = -180;
const ISO_DATE_FORMAT = 'YYYY-MM-DD';

const nowInBrt = (): moment.Moment => moment().utcOffset(BRT_UTC_OFFSET_MINUTES);

const parseBrtDate = (date: string): moment.Moment =>
  moment.parseZone(`${date}T00:00:00-03:00`, `${ISO_DATE_FORMAT}THH:mm:ssZ`, true);

export const todayInBrt = (): string => nowInBrt().format(ISO_DATE_FORMAT);

export const formatCurrency = (
  value: string | number | undefined,
  options: FormatCurrencyOptions = {},
): string => {
  if (typeof value === 'undefined') return '';
  const parsed = typeof value === 'number' ? value : parseFloat(value);

  if (value === '' || value === null || value === undefined || Number.isNaN(parsed)) {
    return '';
  }

  if (options.emptyOnZero && parsed === 0) {
    return '';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

export const formatPercentage = (value: number): string => {
  if (!value) return '';
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
};

export const parseCurrencyInput = (rawValue: string): number => {
  const cleanValue = rawValue.replace(/\D/g, '');
  if (!cleanValue) return 0;
  return parseFloat((parseInt(cleanValue, 10) / 100).toFixed(2));
};

export const parsePercentageInput = (rawValue: string): number => {
  const cleanValue = rawValue.replace(/[^\d,]/g, '').replace(',', '.');
  return cleanValue ? parseFloat(cleanValue) : 0;
};

export const calculateCoverage = (
  value: number,
  percentage: number,
  additionalValue = 0,
): number => {
  if (!value || !percentage) return 0;
  return Math.round(value * percentage) / 100 + additionalValue;
};

export const calculateEndDate = (startDate: string, contractDays: number): string => {
  if (!startDate || contractDays <= 0) return '';
  const start = parseBrtDate(startDate);
  if (!start.isValid()) return '';
  return start.add(contractDays, 'days').format(ISO_DATE_FORMAT);
};

export const calculateMinStartDate = (typeWarrantyId?: string): string => {
  const hd = new Holidays('BR');
  const minStartDate = nowInBrt().startOf('day');
  const dayOfWeek = minStartDate.day();

  const isHoliday = (date: moment.Moment): boolean => {
    return hd.isHoliday(date.clone().startOf('day').add(12, 'hours').toDate()) !== false;
  };

  if (typeWarrantyId === 'Licitante') {
    if (dayOfWeek === 0) {
      const monday = minStartDate.clone().add(1, 'day');

      if (isHoliday(monday)) {
        minStartDate.add(2, 'days');
      } else {
        minStartDate.add(1, 'day');
      }
    } else if (dayOfWeek === 1) {
      if (isHoliday(minStartDate)) {
        minStartDate.add(1, 'day');
      }
    } else {
      const yesterday = minStartDate.clone().subtract(1, 'day');

      if (!isHoliday(yesterday)) {
        minStartDate.subtract(1, 'day');
      }
    }
  } else {
    minStartDate.subtract(180, 'days');
  }

  return minStartDate.format(ISO_DATE_FORMAT);
};

export const formatCNPJ = (cnpj: string | undefined): string => {
  if (!cnpj) return '';
  const clean = cleanCnpj(cnpj);
  if (clean.length !== 14) return cnpj;
  return maskCnpj(clean);
};

export const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const date = moment(dateString, 'YYYY-MM-DD');
  return date.isValid() ? date.format('DD/MM/YYYY') : '-';
};

export const formatInsuranceName = (name: string): string => {
  if (!name) return 'N/A';
  switch (name) {
    case 'junto':
      return 'Junto Seguros';
    case 'pottencial':
      return 'Pottencial';
    case 'sombrero':
      return 'Sombrero Seguradora';
    case 'avla':
      return 'Avla Seguradora';
    default:
      return name;
  }
};

export const STATUS_LABEL_MAP: Record<InsuranceStatus, string> = {
  cotacao: 'Cotação',
  analise: 'Em análise',
  emitida: 'Emitida',
  vigente: 'Vigente',
  encerrada: 'Encerrada',
  cancelada: 'Cancelada',
  recusada: 'Recusada',
  expirada: 'Expirada',
};

const STATUS_CLASS_MAP: Record<InsuranceStatus, string> = {
  cotacao: 'badge-secondary',
  analise: 'badge-warning',
  emitida: 'badge-success',
  vigente: 'badge-success',
  encerrada: 'badge-warning',
  cancelada: 'badge-danger',
  recusada: 'badge-danger',
  expirada: 'badge-warning',
};

export const formatStatus = (status: string): { text: string; className: string } => {
  if (!status) return { text: 'N/A', className: 'badge-secondary' };
  const insuranceStatus = status as InsuranceStatus;
  return {
    text: STATUS_LABEL_MAP[insuranceStatus] ?? status,
    className: STATUS_CLASS_MAP[insuranceStatus] ?? 'badge-secondary',
  };
};

export const INSURANCE_REASON_MAP: { [key: string]: string } = {
  Licitante: 'Vou participar de uma licitação',
  'Executante Construtor': 'Venci uma licitação de obras/construção',
  'Executante Prestador de Serviços': 'Venci uma licitação de serviços',
  'Executante Fornecedor': 'Venci uma licitação de equipamentos/produtos',
};

export const getInsuranceReason = (type_warranty_id: string): string =>
  INSURANCE_REASON_MAP[type_warranty_id || ''] || '';

export const getInsuranceReasonOptions = (): { value: string; text: string }[] =>
  Object.entries(INSURANCE_REASON_MAP).map(([value, text]) => ({ value, text }));

export const modalities = [
  { label: 'Pregão Eletrônico', value: 'Pregão Eletrônico' },
  { label: 'Pregão Presencial', value: 'Pregão Presencial' },
  { label: 'Concorrência Eletrônica', value: 'Concorrência Eletrônica' },
  { label: 'Concorrência', value: 'Concorrência' },
  { label: 'Tomada de Preços', value: 'Tomada de Preços' },
  { label: 'Dispensa de Licitação', value: 'Dispensa de Licitação' },
  { label: 'Inexigibilidade', value: 'Inexigibilidade' },
  { label: 'Contratação Emergencial', value: 'Contratação Emergencial' },
] as const;

export type Modality = typeof modalities[number]['value'];

const COMBINING_MARKS = /[\u0300-\u036f]/g;

const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const MODALITY_RULES: { value: Modality; terms: string[] }[] = [
  { value: 'Pregão Eletrônico', terms: ['pregao eletronico'] },
  { value: 'Pregão Presencial', terms: ['pregao presencial'] },
  { value: 'Concorrência Eletrônica', terms: ['concorrencia eletronica'] },
  { value: 'Concorrência', terms: ['concorrencia'] },
  { value: 'Tomada de Preços', terms: ['tomada de preco'] },
  { value: 'Contratação Emergencial', terms: ['emergencial', 'emergencia'] },
  { value: 'Inexigibilidade', terms: ['inexigibilidade', 'inexigivel'] },
  { value: 'Dispensa de Licitação', terms: ['dispensa', 'contratacao direta'] },
  { value: 'Pregão Eletrônico', terms: ['pregao'] },
];

export const normalizeModality = (modality?: string): string => {
  const text = normalizeText(modality || '');
  if (!text) return '';

  const rule = MODALITY_RULES.find(({ terms }) => terms.some((term) => text.includes(term)));

  return rule ? rule.value : '';
};

export const sanitizeCnpj = (cnpj: string) => {
  return cleanCnpj(cnpj);
};

export const applyMask = (value: string, mask: string) => {
  const cleanValue = cleanCnpj(value);
  let maskedValue = '';
  let valueIndex = 0;

  for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i += 1) {
    if (mask[i] === '#') {
      maskedValue += cleanValue[valueIndex];
      valueIndex += 1;
    } else {
      maskedValue += mask[i];
    }
  }

  return maskedValue;
};

export const filledFieldNames = (fields: Record<string, unknown>): string[] =>
  Object.entries(fields || {})
    .filter(([, value]) =>
      typeof value === 'number' ? value > 0 : String(value ?? '').trim() !== '',
    )
    .map(([key]) => key);

export const hasAutofilledField = (
  autofilledFields: string[] | undefined,
  form: Record<string, unknown>,
): boolean => Object.keys(form).some((field) => (autofilledFields || []).includes(field));

export const QUOTE_INTRO_PATH = '/conlicitaseg/novo';

export const FIDELIZE_QUOTE_PATH = `${QUOTE_INTRO_PATH}?origem=fidelize`;

export const FIDELIZE_EMAIL_ORIGIN = 'fidelize-email';

export const getFidelizeBidding = (
  profile: ProfileData | undefined,
  isOnlyConlicitaseg: boolean,
): BiddingData | null => {
  if (!isOnlyConlicitaseg || !profile?.fidelize?.is_fidelize) return null;

  const { biddings } = profile.fidelize;

  return Array.isArray(biddings) && biddings.length > 0 ? biddings[0] : null;
};

export const buildFidelizeFormQuote = (
  bidding: BiddingData,
  profile: ProfileData,
): Partial<FormProcessData> => ({
  bidding_id: bidding?.id,
  bidding_edict: bidding?.edital,
  bidding_process_number: bidding?.processo,
  bidding_modality: bidding?.modality?.nome,
  contract_value: bidding?.valor,
  insured_name: bidding?.public_body?.nome,
  insured_address: bidding?.orgao_endereco,
  insured_zip_code: bidding?.orgao_cep,
  insured_city: bidding?.orgao_cidade,
  insured_state: bidding?.orgao_estado,
  policyholder_cnpj: profile?.company_data?.data?.cnpj,
  policyholder_company_name: profile?.company_data?.data?.razao_social,
});

// Campos que o pré-preenchimento Fidelize pode aplicar: dados da licitação, do órgão, da
// empresa e as características da licitação ganha (bloco `execucao` da contract-data API).
export const FIDELIZE_AUTOFILL_FIELDS: (keyof FormProcessData)[] = [
  'bidding_id',
  'bidding_edict',
  'bidding_process_number',
  'bidding_modality',
  'bidding_contract_number',
  'contract_days',
  'contract_start_date',
  'contract_percentage',
  'contract_value',
  'insured_cnpj',
  'insured_name',
  'insured_address',
  'insured_zip_code',
  'insured_city',
  'insured_state',
  'policyholder_cnpj',
  'policyholder_company_name',
];

// A contract-data API devolve contract_value como string no formato brasileiro ("1.478.307,00").
export const parseContractValue = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const text = String(value ?? '').trim();
  if (!text) return undefined;

  let normalized = text;
  if (text.includes(',')) normalized = text.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(text)) normalized = text.replace(/\./g, '');

  const parsed = parseFloat(normalized.replace(/[^\d.-]/g, ''));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const pickFidelizeAutofillFields = (
  data: Record<string, unknown> | undefined,
): Record<string, unknown> =>
  FIDELIZE_AUTOFILL_FIELDS.reduce(
    (picked, field) => (data?.[field] === undefined ? picked : { ...picked, [field]: data[field] }),
    {} as Record<string, unknown>,
  );

// Backend é a fonte primária (campos marcados); o payload de login só completa o que o
// backend não enviou, sem marcação. Do backend vale o bloco `execucao` (licitação ganha →
// garantia de execução). Campos sem dado em nenhum dos dois ficam de fora.
export const mergeFidelizeAutofill = (
  backendData: Record<string, unknown> | undefined,
  fallback: Partial<FormProcessData>,
): { form: Partial<FormProcessData>; autofilled: string[] } => {
  const execucao = (backendData?.execucao || {}) as Record<string, unknown>;
  const orgaoCnpj = sanitizeCnpj(String(backendData?.orgao_cnpj || ''));
  const fromBackend = pickFidelizeAutofillFields({
    ...backendData,
    ...execucao,
    contract_value: parseContractValue(execucao.contract_value ?? backendData?.contract_value),
    ...(orgaoCnpj ? { insured_cnpj: orgaoCnpj } : {}),
  });
  const fromFallback = pickFidelizeAutofillFields(fallback as Record<string, unknown>);
  const autofilled = filledFieldNames(fromBackend);

  const form: Record<string, unknown> = {};
  filledFieldNames(fromFallback).forEach((field) => {
    form[field] = fromFallback[field];
  });
  autofilled.forEach((field) => {
    form[field] = fromBackend[field];
  });

  return { form: form as Partial<FormProcessData>, autofilled };
};
